import {
  Button,
  CircularProgress,
  IconButton,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Text,
} from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useParams } from '@tanstack/react-router';
import * as turf from '@turf/turf';
import type { FeatureCollection } from 'geojson';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import MapBox, {
  Layer,
  type MapMouseEvent,
  type MapRef,
  NavigationControl,
  Popup,
  Source,
  type ViewState,
} from 'react-map-gl/mapbox';
import { useAsyncRetry } from 'react-use';
import {
  type AgencySubGeometry,
  type AgencySubGeometryMemberContact,
  type AgencySubGeometryMemberLink,
  getAgencyGeometries as apiGetAgencyGeometries,
  getAgencySubGeometries as apiGetAgencySubGeometries,
} from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import getConfig from '~/config';
import ArchiveSubGeometryDialog from './ArchiveSubGeometryDialog';
import {
  buildSubGeometryFeatureCollection,
  canManageSubRegions,
  fitBoundsInitialViewState,
  SUB_GEO_FILL_LAYER_ID,
  SUB_GEO_SOURCE_ID,
  subGeometryDisplayName,
} from './helpers';
import ManageSubGeometryDialog from './ManageSubGeometryDialog';
import SubGeometryDrawView from './SubGeometryDrawView';

const config = getConfig();
const BACKDROP_SOURCE_ID = 'agency-footprint-source';
const NONE = '__none__';
const EMPTY_FC: FeatureCollection = { type: 'FeatureCollection', features: [] };

export default function SubGeometries() {
  const { agencyId } = useParams({ strict: false }) as { agencyId: string };
  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Which surface owns the detail card for the current selection: the list popover or the map
  // popup. Tracking the source keeps exactly one card open (never both) regardless of where the
  // selection came from.
  const [detailSurface, setDetailSurface] = useState<'list' | 'map'>('list');
  const [cursor, setCursor] = useState<'auto' | 'pointer'>('auto');
  const [mode, setMode] = useState<'list' | 'draw'>('list');
  const [editingSubGeometry, setEditingSubGeometry] = useState<AgencySubGeometry | null>(null);
  // Which sub-geometry is currently open in the Manage dialog (lifted out of the detail card so
  // it persists after the popover/popup closes).
  const [managingId, setManagingId] = useState<string | null>(null);
  // Same lift for the Archive confirm dialog.
  const [archivingId, setArchivingId] = useState<string | null>(null);
  const canManage = canManageSubRegions(useFeatureFlagEnabled('agency-edit-sub-regions'));
  const mapRef = useRef<MapRef | null>(null);
  const setMapRef = useCallback((map: MapRef | null) => {
    mapRef.current = map;
  }, []);
  const rowRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  // Keyed on the agency we've already reported a view for, so the "viewed" event fires again when
  // the user switches to a different agency without the component unmounting.
  const viewedAgencyRef = useRef<string | null>(null);

  const fetchState = useAsyncRetry(async () => {
    if (!agencyId) throw new Error('agencyId missing from route params');
    const [subRes, geomRes] = await Promise.all([
      apiGetAgencySubGeometries(agencyId),
      apiGetAgencyGeometries(agencyId),
    ]);
    return {
      subGeometries: subRes.subGeometries,
      backdrop: geomRes as FeatureCollection,
      contacts: subRes.contacts,
      links: subRes.links,
    };
  }, [agencyId]);

  const subGeometries: AgencySubGeometry[] = fetchState.value?.subGeometries ?? [];
  const backdrop: FeatureCollection = fetchState.value?.backdrop ?? EMPTY_FC;
  const agencyContacts: AgencySubGeometryMemberContact[] = fetchState.value?.contacts ?? [];
  const agencyLinks: AgencySubGeometryMemberLink[] = fetchState.value?.links ?? [];
  const selected = subGeometries.find((sg) => sg.id === selectedId) ?? null;
  // Derive the live sub-geometry for the Manage dialog so it reflects refreshed data after onChanged.
  const managingSubGeometry = managingId ? (subGeometries.find((sg) => sg.id === managingId) ?? null) : null;
  const archivingSubGeometry = archivingId ? (subGeometries.find((sg) => sg.id === archivingId) ?? null) : null;

  useEffect(() => {
    if (fetchState.value && viewedAgencyRef.current !== agencyId) {
      viewedAgencyRef.current = agencyId;
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometriesViewed, {
        agency_id: agencyId,
        sub_geometry_count: fetchState.value.subGeometries.length,
      });
    }
  }, [fetchState.value, agencyId]);

  const onSelect = useCallback(
    (id: string | null, source: 'list' | 'map' = 'list') => {
      setSelectedId(id);
      if (id) {
        setDetailSurface(source);
        capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometrySelected, {
          agency_id: agencyId,
          sub_geometry_id: id,
        });
      }
    },
    [agencyId],
  );

  const featureCollection = useMemo(() => buildSubGeometryFeatureCollection(subGeometries), [subGeometries]);

  const selectedCenter = useMemo(() => {
    if (!selected) return null;
    const c = turf.centroid(selected.geometry).geometry.coordinates as [number, number];
    return { longitude: c[0], latitude: c[1] };
  }, [selected]);

  useEffect(() => {
    if (!selectedId) return;
    const sg = subGeometries.find((s) => s.id === selectedId);
    if (!sg) return;
    rowRefs.current[selectedId]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    const map = mapRef.current;
    if (!map) return;
    const target = turf.bbox(sg.geometry) as [number, number, number, number];
    const view = map.getBounds();
    // Only re-frame the map when the sub-geometry isn't already fully in view.
    if (view) {
      const fullyVisible =
        target[0] >= view.getWest() &&
        target[1] >= view.getSouth() &&
        target[2] <= view.getEast() &&
        target[3] <= view.getNorth();
      if (fullyVisible) return;
    }
    map.fitBounds(target, { padding: 80, duration: 800, maxZoom: 16 });
  }, [selectedId, subGeometries]);

  // Mount the map already framed on the service area (footprint backdrop, else the sub-geometries),
  // so it doesn't flash the US view and then zoom in.
  const initialViewState = useMemo(
    () =>
      fitBoundsInitialViewState([backdrop.features.length > 0 ? backdrop : featureCollection], {
        padding: 40,
        maxZoom: 15,
      }),
    [backdrop, featureCollection],
  );

  const handleMapClick = useCallback(
    (e: MapMouseEvent) => {
      const id = e.features?.find((f) => f.layer?.id === SUB_GEO_FILL_LAYER_ID)?.properties?.id;
      if (typeof id === 'string') onSelect(id, 'map');
      // Clicking empty map space clears the current selection.
      else onSelect(null);
    },
    [onSelect],
  );

  const handleMouseMove = useCallback((e: MapMouseEvent) => {
    const over = e.features?.some((f) => f.layer?.id === SUB_GEO_FILL_LAYER_ID);
    setCursor(over ? 'pointer' : 'auto');
  }, []);

  // Only show the full-page spinner on the initial (valueless) load. `useAsyncRetry`
  // retains the previous value during a retry, so background refreshes triggered by
  // mutations (e.g. from the Manage dialog's onChanged) keep the list — and the
  // tab-level Manage dialog — mounted instead of flashing the spinner.
  if (fetchState.loading && !fetchState.value) {
    return (
      <div className="flex h-full items-center justify-center" data-testid="agency-sub-geometries-loading">
        <CircularProgress indeterminate />
      </div>
    );
  }

  if (fetchState.error && !fetchState.value) {
    return (
      <div
        className="flex h-full flex-col items-center justify-center space-y-2"
        data-testid="agency-sub-geometries-error"
      >
        <div>Error loading sub regions for this agency.</div>
        <Button variant="secondary" onClick={() => fetchState.retry()} data-testid="agency-sub-geometries-retry">
          Try again
        </Button>
      </div>
    );
  }

  if (mode === 'draw') {
    return (
      <SubGeometryDrawView
        agencyId={agencyId}
        footprint={backdrop}
        existingSubGeometries={subGeometries}
        subGeometry={editingSubGeometry ?? undefined}
        onDone={() => {
          setEditingSubGeometry(null);
          setMode('list');
          fetchState.retry();
        }}
      />
    );
  }

  return (
    <div
      className="grid h-full min-h-100 grid-cols-[280px_1fr] grid-rows-[minmax(0,1fr)] gap-2 overflow-hidden p-2"
      data-testid="agency-sub-geometries-page"
    >
      <aside
        className="flex flex-col gap-1 overflow-y-auto rounded border border-grey-30 p-2"
        data-testid="agency-sub-geometries-list"
      >
        {canManage && (
          <Button
            className="mb-1"
            onClick={() => {
              setEditingSubGeometry(null);
              setMode('draw');
            }}
            data-testid="agency-sub-geometries-draw-button"
          >
            Draw sub region
          </Button>
        )}
        {subGeometries.length === 0 ? (
          <Text className="p-2 text-xs text-grey-60" data-testid="agency-sub-geometries-empty">
            No sub regions for this agency yet.
          </Text>
        ) : (
          subGeometries.map((sg) => {
            const isSelected = sg.id === selectedId;
            return (
              <Popover
                key={sg.id}
                open={isSelected && detailSurface === 'list'}
                onOpenChange={(open) => {
                  if (!open) onSelect(null);
                }}
              >
                <PopoverTrigger asChild>
                  <button
                    ref={(el) => {
                      rowRefs.current[sg.id] = el;
                    }}
                    type="button"
                    onClick={() => onSelect(isSelected && detailSurface === 'list' ? null : sg.id, 'list')}
                    className={cn(
                      'flex flex-col items-start gap-0.5 rounded border px-2 py-1.5 text-left transition-colors',
                      isSelected ? 'border-brand bg-brand/10' : 'border-grey-20 hover:bg-brand/5',
                    )}
                    data-testid={`agency-sub-geometries-row-${sg.id}`}
                    data-selected={isSelected}
                  >
                    <Text className="text-sm font-semibold">{subGeometryDisplayName(sg.name)}</Text>
                    <Text className="text-xs text-grey-60">
                      {sg.contacts.length} contact{sg.contacts.length === 1 ? '' : 's'} · {sg.links.length} link
                      {sg.links.length === 1 ? '' : 's'} · {sg.notes.length} note{sg.notes.length === 1 ? '' : 's'}
                    </Text>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  side="right"
                  align="start"
                  sideOffset={12}
                  className="w-80"
                  onInteractOutside={(e) => e.preventDefault()}
                  onOpenAutoFocus={(e) => e.preventDefault()}
                  data-testid={`agency-sub-geometries-detail-${sg.id}`}
                >
                  <SubGeometryDetail
                    subGeometry={sg}
                    canManage={canManage}
                    onClose={() => onSelect(null)}
                    onEdit={() => {
                      setEditingSubGeometry(sg);
                      setMode('draw');
                    }}
                    onManage={(id) => {
                      setManagingId(id);
                      onSelect(null);
                    }}
                    onArchive={(id) => {
                      setArchivingId(id);
                      onSelect(null);
                    }}
                  />
                </PopoverContent>
              </Popover>
            );
          })
        )}
      </aside>

      <div
        className="relative h-full min-h-100 overflow-hidden rounded"
        data-testid="agency-sub-geometries-map-container"
      >
        <MapBox
          ref={setMapRef}
          mapboxAccessToken={config.mapboxToken}
          initialViewState={initialViewState as Partial<ViewState> & typeof initialViewState}
          mapStyle="mapbox://styles/mapbox/streets-v12"
          attributionControl={false}
          interactiveLayerIds={subGeometries.length > 0 ? [SUB_GEO_FILL_LAYER_ID] : undefined}
          cursor={cursor}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          style={{ position: 'absolute', inset: 0 }}
        >
          <NavigationControl position="top-right" showCompass={false} />

          {backdrop.features.length > 0 && (
            <Source id={BACKDROP_SOURCE_ID} type="geojson" data={backdrop}>
              <Layer
                id="agency-footprint-fill"
                source={BACKDROP_SOURCE_ID}
                type="fill"
                paint={{ 'fill-color': '#6b7280', 'fill-opacity': 0.08 }}
              />
              <Layer
                id="agency-footprint-line"
                source={BACKDROP_SOURCE_ID}
                type="line"
                paint={{ 'line-color': '#6b7280', 'line-width': 1, 'line-dasharray': [2, 2] }}
              />
            </Source>
          )}

          {featureCollection.features.length > 0 && (
            <Source id={SUB_GEO_SOURCE_ID} type="geojson" data={featureCollection}>
              <Layer
                id={SUB_GEO_FILL_LAYER_ID}
                source={SUB_GEO_SOURCE_ID}
                type="fill"
                paint={{ 'fill-color': '#2563eb', 'fill-opacity': 0.25 }}
              />
              <Layer
                id="agency-sub-geometries-line"
                source={SUB_GEO_SOURCE_ID}
                type="line"
                paint={{ 'line-color': '#2563eb', 'line-width': 2 }}
              />
              <Layer
                id="agency-sub-geometries-selected-fill"
                source={SUB_GEO_SOURCE_ID}
                type="fill"
                filter={['==', ['get', 'id'], selected?.id ?? NONE]}
                paint={{ 'fill-color': '#2563eb', 'fill-opacity': 0.5 }}
              />
              <Layer
                id="agency-sub-geometries-selected-line"
                source={SUB_GEO_SOURCE_ID}
                type="line"
                filter={['==', ['get', 'id'], selected?.id ?? NONE]}
                paint={{ 'line-color': '#1d4ed8', 'line-width': 4 }}
              />
            </Source>
          )}

          {selected && selectedCenter && detailSurface === 'map' && (
            <Popup
              key={selected.id}
              anchor="bottom"
              longitude={selectedCenter.longitude}
              latitude={selectedCenter.latitude}
              maxWidth="320px"
              closeButton={false}
              closeOnClick={false}
              closeOnMove={false}
              focusAfterOpen={false}
            >
              <div className="w-72" data-testid={`agency-sub-geometries-map-popup-${selected.id}`}>
                <SubGeometryDetail
                  subGeometry={selected}
                  canManage={canManage}
                  onClose={() => onSelect(null)}
                  onEdit={() => {
                    setEditingSubGeometry(selected);
                    setMode('draw');
                  }}
                  onManage={(id) => {
                    setManagingId(id);
                    onSelect(null);
                  }}
                  onArchive={(id) => {
                    setArchivingId(id);
                    onSelect(null);
                  }}
                />
              </div>
            </Popup>
          )}
        </MapBox>
      </div>

      {/* Manage dialog — rendered once outside the popover/popup so it survives close */}
      {managingSubGeometry && (
        <ManageSubGeometryDialog
          isOpen
          onOpenChange={(open) => {
            if (!open) setManagingId(null);
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            rowRefs.current[managingSubGeometry.id]?.focus();
          }}
          agencyId={agencyId}
          subGeometry={managingSubGeometry}
          contacts={agencyContacts}
          links={agencyLinks}
          onChanged={() => fetchState.retry()}
        />
      )}

      {/* Archive confirm dialog — also lifted to page level so it survives the detail card closing */}
      {archivingSubGeometry && (
        <ArchiveSubGeometryDialog
          isOpen
          onOpenChange={(open) => {
            if (!open) setArchivingId(null);
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            rowRefs.current[archivingSubGeometry.id]?.focus();
          }}
          agencyId={agencyId}
          subGeometry={archivingSubGeometry}
          onArchived={() => {
            setArchivingId(null);
            fetchState.retry();
          }}
        />
      )}
    </div>
  );
}

function SubGeometryDetail({
  subGeometry,
  canManage,
  onClose,
  onEdit,
  onManage,
  onArchive,
}: {
  subGeometry: AgencySubGeometry;
  canManage: boolean;
  onClose: () => void;
  onEdit: () => void;
  onManage: (id: string) => void;
  onArchive: (id: string) => void;
}) {
  return (
    <div className="relative flex flex-col gap-2">
      <IconButton
        icon="cross2"
        variant="hollow"
        size="icon"
        className="absolute right-0 top-0"
        type="button"
        onClick={onClose}
        data-testid={`agency-sub-geometries-detail-close-${subGeometry.id}`}
        aria-label="Close sub region details"
      />
      <Text className="pr-6 text-sm font-semibold">{subGeometryDisplayName(subGeometry.name)}</Text>

      <DetailSection
        label={`Contacts (${subGeometry.contacts.length})`}
        testId={`agency-sub-geometries-contacts-${subGeometry.id}`}
      >
        {subGeometry.contacts.length > 0 ? (
          subGeometry.contacts.map((c) => (
            <div key={c.id} className="flex flex-col" data-testid={`agency-sub-geometries-contact-${c.id}`}>
              <Text className="text-xs">
                {c.firstName} {c.lastName}
                {c.title ? ` — ${c.title}` : ''}
              </Text>
              <Text className="text-xs text-grey-60">{c.email}</Text>
            </div>
          ))
        ) : (
          <Text className="text-xs text-grey-60">No contacts</Text>
        )}
      </DetailSection>

      <DetailSection
        label={`Links (${subGeometry.links.length})`}
        testId={`agency-sub-geometries-links-${subGeometry.id}`}
      >
        {subGeometry.links.length > 0 ? (
          subGeometry.links.map((l) => (
            <div key={l.id} className="flex flex-col" data-testid={`agency-sub-geometries-link-${l.id}`}>
              <Text className="text-xs">
                {l.title} <span className="text-grey-60">({l.type})</span>
              </Text>
              <Text className="truncate text-xs text-grey-60">{l.url}</Text>
            </div>
          ))
        ) : (
          <Text className="text-xs text-grey-60">No links</Text>
        )}
      </DetailSection>

      <DetailSection
        label={`Notes (${subGeometry.notes.length})`}
        testId={`agency-sub-geometries-notes-${subGeometry.id}`}
      >
        {subGeometry.notes.length > 0 ? (
          subGeometry.notes.map((n) => (
            <Text key={n.id} className="whitespace-pre-wrap text-xs" data-testid={`agency-sub-geometries-note-${n.id}`}>
              {n.notes}
            </Text>
          ))
        ) : (
          <Text className="text-xs text-grey-60">No notes</Text>
        )}
      </DetailSection>

      <div className="flex justify-end gap-2">
        {canManage && (
          <Button
            variant="secondary"
            className="self-start"
            onClick={onEdit}
            data-testid={`agency-sub-geometries-edit-${subGeometry.id}`}
          >
            Edit
          </Button>
        )}
        <Button
          variant="secondary"
          className="self-start"
          onClick={() => onManage(subGeometry.id)}
          data-testid={`agency-sub-geometries-manage-${subGeometry.id}`}
        >
          Manage
        </Button>
        {canManage && (
          <Button
            variant="danger"
            className="self-start"
            onClick={() => onArchive(subGeometry.id)}
            data-testid={`agency-sub-geometries-archive-${subGeometry.id}`}
          >
            Archive
          </Button>
        )}
      </div>
    </div>
  );
}

function DetailSection({ label, testId, children }: { label: string; testId: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Text className="text-xs font-medium text-grey-70">{label}</Text>
      <div className="flex flex-col gap-1.5" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}
